"use client";

import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

export function HeliosGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 67 70" fill="none" className={className} aria-hidden="true">
      <path
        d="M15.4546 40C7.4214 40 0.90918 46.5122 0.90918 54.5455C0.90918 62.5787 7.4214 69.0909 15.4546 69.0909C23.4879 69.0909 30.0001 62.5787 30.0001 54.5455C30.0001 46.5122 23.4879 40 15.4546 40Z"
        fill="#00BBFB"
      />
      <path
        d="M16.626 0C7.44371 0 0 7.27742 0 16.2546V40C1.93304 33.6328 8.00701 29.3347 14.7976 29.5288H50.6143C58.5519 29.6573 65.312 23.915 66.2905 16.2128C66.7093 12.0651 65.3152 7.93751 62.4524 4.84916C59.5896 1.76081 55.5249 -0.000521285 51.2617 0H16.626Z"
        fill="#6B00F0"
      />
    </svg>
  );
}

/**
 * iOS-style Helios app icon with an unread notification badge. The badge counts
 * up 1 → 4 with a playful pop and a staggered, irregular cadence once the icon
 * scrolls into view, hinting at the member activity piling up unanswered.
 */
export function MemberDataGapIcon() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { amount: 0.6 });
  const reduce = useReducedMotion() ?? false;
  const [count, setCount] = useState(0);

  useEffect(() => {
    // Reset when scrolled out so the count-up replays on every re-entry.
    if (!inView) {
      setCount(0);
      return;
    }
    if (reduce) {
      setCount(4);
      return;
    }
    // Irregular cadence so the count feels like real activity trickling in —
    // a beat after 1, a quick 2–3 burst, then a pause before 4 — rather than a
    // mechanical, evenly-spaced metronome.
    const stepDelays = [450, 1300, 1650, 2600]; // cumulative ms for counts 1–4
    const timers = stepDelays.map((delay, i) => setTimeout(() => setCount(i + 1), delay));
    return () => {
      for (const t of timers) clearTimeout(t);
    };
  }, [inView, reduce]);

  return (
    <div ref={ref} className="mb-6 flex justify-center">
      {/* Slight group tilt for a playful, off-grid feel. */}
      <div className="relative -rotate-6">
        <motion.div
          initial={reduce ? false : { opacity: 0, scale: 0.8, y: 8 }}
          animate={
            reduce
              ? undefined
              : inView
                ? { opacity: 1, scale: 1, y: 0 }
                : { opacity: 0, scale: 0.8, y: 8 }
          }
          transition={{ type: "spring", stiffness: 320, damping: 22 }}
          className="flex size-16 items-center justify-center rounded-[22%] bg-white shadow-[0_10px_30px_-8px_rgba(17,12,46,0.35)] ring-1 ring-black/5"
        >
          <HeliosGlyph className="size-9" />
        </motion.div>

        <AnimatePresence>
          {count > 0 ? (
            <motion.div
              initial={reduce ? false : { scale: 0, opacity: 0 }}
              animate={{ scale: count > 1 ? [1, 1.22, 1] : 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 520, damping: 16 }}
              className="absolute -right-2.5 -top-2.5 flex h-6 min-w-6 rotate-12 items-center justify-center rounded-full bg-red-500 px-1.5 text-[12px] font-bold leading-none text-white shadow-sm ring-2 ring-background"
            >
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                  key={count}
                  initial={reduce ? false : { y: -6, opacity: 0, scale: 0.4 }}
                  animate={{ y: 0, opacity: 1, scale: 1 }}
                  exit={reduce ? undefined : { y: 6, opacity: 0, scale: 0.4 }}
                  transition={{ type: "spring", stiffness: 600, damping: 24 }}
                  className="tabular-nums"
                >
                  {count}
                </motion.span>
              </AnimatePresence>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
}
